import {
    isStandalone,
    LLM_BASE_URL,
    LLM_MODEL,
    LLM_PROVIDER,
    LLM_MAX_TOKENS,
    PPAI_STANDALONE_AUTH_REQUIRED,
    PPAI_DAILY_LIMIT_REACHED
} from "../config/ppAiConfig"
import PPUtils from "./PPUtils"
import { getStandaloneToken, clearStandaloneToken } from "./ppAiStandaloneAuth"

function convertOpenAiToolsToAnthropic(tools = []) {
    return tools.map((tool) => ({
        name: tool.function?.name,
        description: tool.function?.description,
        input_schema: tool.function?.parameters || { type: "object", properties: {} }
    }))
}

function buildAnthropicMessages(messages = []) {
    const result = []
    for (const msg of messages) {
        if (!msg || (!msg.role && !msg.tool_calls)) {
            continue
        }
        if (msg.role === "system") continue
        if (msg.role === "tool") {
            result.push({
                role: "user",
                content: [
                    {
                        type: "tool_result",
                        tool_use_id: msg.tool_call_id,
                        content: msg.content
                    }
                ]
            })
            continue
        }
        if ((!msg.role || msg.role === "assistant") && Array.isArray(msg.tool_calls)) {
            const contentBlocks = []
            if (msg.content) {
                contentBlocks.push({ type: "text", text: msg.content })
            }
            for (const tc of msg.tool_calls) {
                let input = {}
                try {
                    input = JSON.parse(tc.function?.arguments || "{}")
                } catch {
                    input = {}
                }
                contentBlocks.push({
                    type: "tool_use",
                    id: tc.id,
                    name: tc.function?.name,
                    input
                })
            }
            result.push({
                role: "assistant",
                content: contentBlocks
            })
            continue
        }
        if (msg.role) {
            result.push({ role: msg.role, content: msg.content })
        }
    }
    return result
}

function buildAnthropicPayload(messages, model, tools) {
    const systemMessages = messages.filter((m) => m.role === "system")
    const system = systemMessages.map((m) => m.content).join("\n\n")
    const filteredMessages = buildAnthropicMessages(messages)
    const payload = {
        model,
        max_tokens: LLM_MAX_TOKENS,
        messages: filteredMessages
    }
    if (system) payload.system = system
    if (tools?.length) payload.tools = convertOpenAiToolsToAnthropic(tools)
    return payload
}

export async function summarizeConversation(messages, t = (s) => s) {
    if (!Array.isArray(messages) || messages.length === 0) return ""
    const truncated = messages.map((m) => {
        const role = m.role === "user" ? "Nutzer" : "Assistent"
        const text = (m.content || "").slice(0, 600)
        return `${role}: ${text}`
    })
    const prompt = truncated.join("\n\n")
    const summaryMessages = [
        {
            role: "system",
            content: t("ppai.summary_system") || "Fasse die Konversation in maximal 150 Wörtern zusammen. Behalte wichtige Fakten, Produktnummern (GTIN/EAN), Preise und Entscheidungen bei."
        },
        { role: "user", content: prompt }
    ]
    const res = await chatCompletions(summaryMessages, { model: LLM_MODEL })
    const text = res?.choices?.[0]?.message?.content?.trim() || ""
    return text
}

export async function chatCompletions(messages, options = {}) {
    const { model = LLM_MODEL, tools, tool_choice, signal } = options
    const isAnthropic = LLM_PROVIDER === "anthropic"
    const body = isAnthropic
        ? buildAnthropicPayload(messages, model, tools)
        : {
              model,
              messages,
              stream: false,
              ...(tools?.length ? { tools, tool_choice: tool_choice ?? "auto" } : {})
          }
    const token = isStandalone() ? getStandaloneToken() : PPUtils.getLogin()?.token
    const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
    const endpoint = isAnthropic
        ? `${LLM_BASE_URL}/anthropic/messages`
        : `${LLM_BASE_URL}/openai/chat/completions`
    const res = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        signal
    })
    if (!res.ok) {
        if (res.status === 401 && isStandalone()) {
            clearStandaloneToken()
            const e = new Error(PPAI_STANDALONE_AUTH_REQUIRED)
            e.code = PPAI_STANDALONE_AUTH_REQUIRED
            throw e
        }
        const text = await res.text()
        let err = {}
        try {
            err = JSON.parse(text)
        } catch {
            if (text.trimStart().startsWith("<")) {
                throw new Error(`LLM-Server antwortet mit HTML statt JSON (z.B. Fehlerseite). Status: ${res.status}. URL/Backend prüfen.`)
            }
        }
        if (res.status === 429) {
            const detail = (err.detail || err.message || "").toString().toLowerCase()
            if (detail.includes("konversationen") && detail.includes("limit")) {
                const e = new Error(PPAI_DAILY_LIMIT_REACHED)
                e.code = PPAI_DAILY_LIMIT_REACHED
                throw e
            }
        }
        throw new Error(err.error?.message || err.detail || `LLM ${res.status}: ${res.statusText}`)
    }
    const raw = await res.text()
    let data
    try {
        data = JSON.parse(raw)
    } catch (parseErr) {
        if (raw.trimStart().startsWith("<")) {
            throw new Error("LLM-Server antwortet mit HTML statt JSON (z.B. Fehlerseite oder Weiterleitung). Backend/URL prüfen.")
        }
        throw parseErr
    }
    if (isStandalone() && (data?.status === 401 || (data?.error === "Unauthorized" && data?.status === 401))) {
        clearStandaloneToken()
        const e = new Error(PPAI_STANDALONE_AUTH_REQUIRED)
        e.code = PPAI_STANDALONE_AUTH_REQUIRED
        throw e
    }
    if (data?.status === 429 || data?.title === "Too Many Requests") {
        const detail = (data?.detail || data?.message || "").toString().toLowerCase()
        if (detail.includes("konversationen") && detail.includes("limit")) {
            const e = new Error(PPAI_DAILY_LIMIT_REACHED)
            e.code = PPAI_DAILY_LIMIT_REACHED
            throw e
        }
    }
    if (isAnthropic) {
        const content = Array.isArray(data?.content) ? data.content : []
        const text = content
            .filter((c) => c?.type === "text")
            .map((c) => c.text)
            .join("\n")
        const toolCalls = content
            .filter((c) => c?.type === "tool_use")
            .map((c) => ({
                id: c.id,
                type: "function",
                function: {
                    name: c.name,
                    arguments: JSON.stringify(c.input || {})
                }
            }))
        return {
            choices: [
                {
                    message: {
                        content: text,
                        ...(toolCalls.length ? { tool_calls: toolCalls } : {})
                    }
                }
            ]
        }
    }
    return data
}

export async function chatCompletionsStream(messages, options = {}, onDelta) {
    const { model = LLM_MODEL, tools, tool_choice, signal } = options
    const isAnthropic = LLM_PROVIDER === "anthropic"
    const body = isAnthropic
        ? { ...buildAnthropicPayload(messages, model), stream: true }
        : {
              model,
              messages,
              stream: true,
              ...(tools?.length ? { tools, tool_choice: tool_choice ?? "auto" } : {})
          }
    const token = isStandalone() ? getStandaloneToken() : PPUtils.getLogin()?.token
    const headers = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
    const endpoint = isAnthropic
        ? `${LLM_BASE_URL}/anthropic/messages/stream`
        : `${LLM_BASE_URL}/openai/chat/completions/stream`
    const res = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        signal
    })
    if (!res.ok) {
        if (res.status === 401 && isStandalone()) {
            clearStandaloneToken()
            const e = new Error(PPAI_STANDALONE_AUTH_REQUIRED)
            e.code = PPAI_STANDALONE_AUTH_REQUIRED
            throw e
        }
        const errText = await res.text()
        let err = {}
        try {
            err = JSON.parse(errText)
        } catch {
            if (errText.trimStart().startsWith("<")) {
                throw new Error(`LLM-Server antwortet mit HTML statt JSON (z.B. Fehlerseite). Status: ${res.status}. URL/Backend prüfen.`)
            }
        }
        if (res.status === 429) {
            const detail = (err.detail || err.message || "").toString().toLowerCase()
            if (detail.includes("konversationen") && detail.includes("limit")) {
                const e = new Error(PPAI_DAILY_LIMIT_REACHED)
                e.code = PPAI_DAILY_LIMIT_REACHED
                throw e
            }
        }
        throw new Error(err.error?.message || err.detail || `LLM ${res.status}: ${res.statusText}`)
    }
    if (!res.body) return { text: "", didEmit: false }
    const reader = res.body.getReader()
    const decoder = new TextDecoder("utf-8")
    let buffer = ""
    let text = ""
    let didEmit = false
    let reading = true
    while (reading) {
        if (signal?.aborted) {
            reader.cancel()
            reading = false
            continue
        }
        const { value, done } = await reader.read()
        if (done) {
            reading = false
            continue
        }
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split(/\r?\n/)
        buffer = lines.pop() || ""
        for (const line of lines) {
            const trimmed = line.trim()
            if (!trimmed) continue
            if (trimmed.startsWith("event:")) continue
            const payload = trimmed.startsWith("data:") ? trimmed.replace(/^data:\s*/, "") : trimmed
            if (!payload || payload === "[DONE]") continue
            let evt
            try {
                evt = JSON.parse(payload)
            } catch {
                if (payload.trimStart().startsWith("<")) {
                    throw new Error("LLM-Server antwortet mit HTML statt JSON (Stream). Backend/URL prüfen.")
                }
                continue
            }
            if (isStandalone() && (evt?.status === 401 || (evt?.error === "Unauthorized" && evt?.status === 401))) {
                clearStandaloneToken()
                const e = new Error(PPAI_STANDALONE_AUTH_REQUIRED)
                e.code = PPAI_STANDALONE_AUTH_REQUIRED
                throw e
            }
            if (isAnthropic) {
                if (evt.type === "content_block_start" && evt.content_block?.text) {
                    text += evt.content_block.text
                    onDelta?.(text)
                    didEmit = true
                } else if (evt.type === "content_block_delta" && evt.delta?.text) {
                    text += evt.delta.text
                    onDelta?.(text)
                    didEmit = true
                }
            } else if (evt.choices?.length) {
                const deltaText = evt.choices?.[0]?.delta?.content
                if (deltaText) {
                    text += deltaText
                    onDelta?.(text)
                    didEmit = true
                }
            }
        }
    }
    return { text, didEmit }
}
